import { useAppDispatch } from 'reducers/hooks'
import { setAuthenticated, setUserData } from 'reducers/userSlice'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import axios from 'axios'
import { PublicClientApplication, InteractionRequiredAuthError, SilentRequest, EventType, AuthenticationResult, AccountInfo } from '@azure/msal-browser';
import { RedirectionType } from './types'

//authenticationUtilities.tsx
//Utility that main task is to interact with IDP.
//OIDC should not be used outside of this utility in order to:
//  - provide consistent interaction with IDP,
//  - make sure that only one instance of UserManager is created,
//  - avoid issues caused by calling or redirecting to IDP from multiple places in application,
//  - make debugging easier,
//  - make implementing new functionalities easier,

let msalInstance: PublicClientApplication | null = null;

const getMsalInstance = async (config: any) => {
  if (msalInstance) return msalInstance;

  msalInstance = new PublicClientApplication({
    auth: {
      clientId: config.client_id,
      authority: config.authority,
      knownAuthorities: ['id.milestonelogin.xyz', 'id.milestonelogin.com'],
      redirectUri: config.redirect_uri,
      postLogoutRedirectUri: config.post_logout_redirect_uri
    },
    cache: {
      cacheLocation: config.cacheLocation || 'sessionStorage',
      storeAuthStateInCookie: config.storeAuthStateInCookie || false
    }
  });
  await msalInstance.initialize()
  return msalInstance;
};

//Custom hook that encapsulates all of the actions needed to be run after successful IDP authentication or if session is still active in the broswer storage.
export const useAuthentication = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  return async (config: any, withOriginPageRedirection?: boolean) => {
    const instance = await getMsalInstance(config);
    const accounts = instance.getAllAccounts();

    if (accounts.length > 0) {
      const request = {
        scopes: config.scopes,
        account: accounts[0]
      };

      try {
        // Try to get token silently
        const response = await instance.acquireTokenSilent(request);
        const account: AccountInfo | undefined = response.account;

        // Attach Bearer token globally to all axios requests
        axios.defaults.headers.common["Authorization"] = `Bearer ${response.accessToken}`;
  
        // Update Redux auth state
        dispatch(setAuthenticated(true));
        dispatch(setUserData({
          name: account?.name || '',
          lastName: '', // might need too redo this later       
          email: account?.username || '',
          companyName: (account?.idTokenClaims as any)?.company_name || ''
        }));

        if (withOriginPageRedirection) {
          redirectToOriginPage(navigate);
        }
      } catch (error) {
        // Token expired or no session found — need to redirect to login
        if (error instanceof InteractionRequiredAuthError) {
          await instance.acquireTokenRedirect({ scopes: config.scopes });
        } else {
          console.error("MSAL token acquisition failed:", error);
          dispatch(setAuthenticated(false));
        }
      }
    } else {
      // No active session
      dispatch(setAuthenticated(false));
    }
  };
};

//Function that encapsulates all possible redirections to IDP.
export const redirectToIDP = async (config: any, redirectionType: RedirectionType, saveOriginPagePath?: boolean) => {
  const instance = await getMsalInstance(config);

  if (saveOriginPagePath) {
    window.localStorage.setItem('originPagePath', window.location.pathname);
  }

  switch (redirectionType) {
    case RedirectionType.Signin:
      await instance.loginRedirect({ scopes: config.scopes });
      break;
    case RedirectionType.Signout:
      await instance.logoutRedirect();
      break;
    case RedirectionType.SigninCallback:
      await instance.handleRedirectPromise();
      break;
  }
};

export const addUserLoadedEvent = async (config: any, callback: () => void) => {
  const instance = await getMsalInstance(config);
  instance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS) {
      callback();
    }
  });
};


const redirectToOriginPage = (navigate: NavigateFunction) => {
  const originPagePath = window.localStorage.getItem('originPagePath')
  window.localStorage.setItem('originPagePath', '')
  navigate(originPagePath ? originPagePath : '/', { replace: true })
}
